import React, {useEffect, useState} from 'react';
import {LoadingIcon, EditLargeIcon} from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import Switch from "../form/Switch";
import FormHeader from "../form/Header";
import Popup from "../Popup";
import { UpdateUser } from "../../api/users/updateUser";
import { SetUserRoles } from "../../api/users/setUserRoles";
import { GetUserRolesForUser } from "../../api/users/getUserRolesForUser";
import RoleSelect from "../form/RoleSelect";
import OfficesSelect from "../form/OfficesSelect";
import CustomPhoneInput from "../form/CustomPhoneInput";
import ResendInviteLink from "../form/ResendInviteLink";
import {DisableUser} from "../../api/users/disableUser";
import {EnableUser} from "../../api/users/enableUser";
import {useTranslation} from "react-i18next";
import useRoleOfficeShape from "../../hooks/useRoleOfficeShape";

const EditUserForm = ({user, onUpdate, onClose, ...props}) => {

    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const [status, setStatus] = useState(user.latest_status);
    const [firstName, setFirstName] = useState(user.first_name);
    const [lastName, setLastName] = useState(user.last_name);
    const [role , setRole] = useState('');
    const [roleName , setRoleName] = useState('');
    const [initialRole , setInitialRole] = useState('');

    const isLegacy = user?.role === user?.role_name;
    
    const [initialOffices , setInitialOffices] = useState([]);
    const [offices , setOffices] = useState([]);
    const [email , setEmail] = useState(user.email);
    const [phone, setPhone] = useState(() => {
        return user.phone_number ? '1' + user.phone_number.toString() : '1';
    });
    const [error, setError] = useState('');

    // Office shape comes from the role's permission bundle (all_offices.read),
    // with the legacy name list as fallback.
    const officeScoped = useRoleOfficeShape(role, roleName);

    const handleStatusChange = (e) => {
        if(e.target.checked){
            setStatus('active');
        } else {
            setStatus('deleted');
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitDisabled(true);
        try {
            // Only office-scoped roles carry offices; all-office roles submit none.
            const officesToSend = officeScoped ? offices : [];
            if (isLegacy) {
                // Legacy role is written to users.role via the profile update.
                await UpdateUser(user.id, firstName, lastName, email, officesToSend, phone.slice(1), role);
            } else {
                await UpdateUser(user.id, firstName, lastName, email, officesToSend, phone.slice(1));
                if(role && role !== initialRole){
                    await SetUserRoles(user.id, [role]);
                }
            }
            if(status === 'deleted' && user.latest_status === 'active'){
                await DisableUser(user.id);
            }
            if(status === 'active' && user.latest_status === 'deleted'){
                await EnableUser(user.id, offices);
            }
            setLoading(false);
            setSubmitDisabled(false);
            onUpdate();
        } catch (err){
            setError(err.message);
            setLoading(false);
            setSubmitDisabled(false);
            console.log("Error fetching data: " + err);
        }
    }

    const handleOfficeChange = (value) => {
        if (Array.isArray(value)){
            setOffices(value)
        } else {
            setOffices([value])
        }
    }

    useEffect(() => {
        if (
            status !== user.latest_status ||
            firstName !== user.first_name ||
            lastName !== user.last_name ||
            role !== initialRole ||
            email !== user.email ||
            phone !== user.phone_number ||
            (!(offices.sort().length === initialOffices.sort().length && offices.sort().every((value, index) => value === initialOffices.sort()[index])))
        ) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [status, firstName, lastName, role, initialRole, email, user, offices, phone]);

    useEffect(() => {
        if(user.user_office){
            const formattedOffices = user.user_office.map(office => office.office_id);
            setInitialOffices(formattedOffices);
            setOffices(formattedOffices);
        }
    }, []);

    useEffect(() => {
        // Legacy: preselect the static role name directly (no dynamic lookup).
        if (isLegacy) {
            setRole(user.role || "");
            setInitialRole(user.role || "");
            setRoleName(user.role || "");
            return;
        }
        // Dynamic: fetch the assigned custom role to get its id (used on save).
        const loadRole = async () => {
            try {
                const response = await GetUserRolesForUser(user.id);
                const current = (response.roles || [])[0];
                if(current){
                    setRole(current.id);
                    setInitialRole(current.id);
                    setRoleName(current.name);
                }
            } catch (err){
                console.log("Error fetching user roles: " + err);
            }
        };
        loadRole();
        // eslint-disable-next-line
    }, []);

    // Note: we intentionally do NOT clear `offices` when the role changes.
    // Clearing it on every role switch discards the user's saved offices and
    // they never come back when switching to an office role again. Instead the
    // selection is preserved and we decide at submit time whether to send it.


    return (
        <Popup size="large" withClose withOverlay onClose={onClose}>
            <form method="post" onSubmit={handleSubmit}>
                <FormHeader title={t("Edit")}
                            description={t("Edit existing user details or disable the user account as needed.")}
                            icon={<EditLargeIcon/>}>
                    <p className="text-right mt-4"><strong>{t("User ID")}: </strong>{user.id}</p>
                </FormHeader>

                <div className="row mb-5 mt-5 align-items-center">
                    <div className="col-xs-6">
                        <Switch title={t("Enabled")}>
                            <input type="checkbox" name="status" id="status" checked={status === 'active'}
                                   onChange={handleStatusChange}/>
                        </Switch>
                    </div>
                    <div className="col-xs-6 text-right">
                        {!user.confirmed && <ResendInviteLink user_id={user.id}/>}

                    </div>
                </div>
                <div className="row mb-5">
                    <div className="col-xs-6">
                        <FormSection title={t("First Name")} required>
                            <input type="text" name="first_name" id="first_name" maxLength="50"
                                   placeholder={t("User first name, max 50 characters")} value={firstName}
                                   onChange={e => setFirstName(e.target.value)}/>
                        </FormSection>
                    </div>
                    <div className="col-xs-6">
                        <FormSection title={t("Last Name")} required>
                            <input type="text" name="last_name" id="last_name" maxLength="50"
                                   placeholder={t("User last name, max 50 characters")} value={lastName}
                                   onChange={e => setLastName(e.target.value)}/>
                        </FormSection>
                    </div>
                </div>
                <div className="row mb-5">
                    <div className="col-xs-6">
                        <FormSection title={t("Email")} required>
                            <input type="email" name="email" id="email" placeholder="user@example.com" value={email}
                                   onChange={e => setEmail(e.target.value)}/>
                        </FormSection>
                    </div>
                    <div className="col-xs-6">
                        <FormSection title={t("Role")} required>
                            <RoleSelect legacy={isLegacy} value={role} onChange={option => { setRole(option.value); setRoleName(option.name); }}/>
                        </FormSection>
                    </div>
                </div>
                {officeScoped && <div className="row mb-5">
                    <div className="col-xs-12">
                        <FormSection title={t("Office")}>
                            <OfficesSelect
                                onChange={handleOfficeChange}
                                defaultOfficeIds={initialOffices}
                                isMulti={roleName !== 'agent'}
                            />
                        </FormSection>
                    </div>
                </div>}
                <div className="row">
                    <div className="col-xs-6">
                        <FormSection title={t("Phone Number")}>
                            <CustomPhoneInput
                                country={'ca'}
                                value={phone}
                                onChange={(value) => setPhone(value)}
                                onlyCountries={['ca']}
                            />
                        </FormSection>
                    </div>
                </div>
                <div className="text-right">
                    <button className={`btn btn-primary btn-medium ${loading ? 'btn-loading' : ''}`} type="submit"
                            name="submit" disabled={submitDisabled}>
                        {loading ? <LoadingIcon/> : ''}
                        {t("Save")}
                    </button>
                </div>
                {error && <p className="color-error text-center mt-3">{error}</p>}
            </form>
        </Popup>
    );
};

export default EditUserForm;